
import React, { useState, useRef, useEffect } from 'react';

import chestPainIcon    from '../../Assets/Images/symcheck/chest pain.svg';
import feverIcon        from '../../Assets/Images/symcheck/fever.svg';
import headacheIcon     from '../../Assets/Images/symcheck/headache.svg';
import stomachPainIcon  from '../../Assets/Images/symcheck/stomach pain.svg';
import robotImg         from '../../Assets/Images/symcheck/internal page robort.png';
import patientChatImg   from '../../Assets/Images/symcheck/patient chat.svg';
import internalPageImge from '../../Assets/Images/rewamp/sym/reply-symptom checker.svg';
import sendSvg          from '../../Assets/Images/rewamp/sym/send.svg';
import Header from '../../components/Header/Header';

const BASE = process.env.REACT_APP_SYMPCHECKER_BASEURL;
const COUPON_API_URL = `${process.env.REACT_APP_QUANTUMURL}coupons/validate?category=CONSULTATION&coupon_code=CB_CONS100`;

const QUICK_SYMPTOMS = [
  { label: 'Chest Pain',   icon: chestPainIcon   },
  { label: 'Fever',        icon: feverIcon        },
  { label: 'Headache',     icon: headacheIcon     },
  { label: 'Stomach Pain', icon: stomachPainIcon  },
];

const CHIP_PATTERNS = {
  nature:   ['Sharp or stabbing', 'Dull or aching', 'Throbbing', 'Burning'],
  duration: ['Less than a day', '1–3 days', 'More than 3 days', 'Chronic'],
  severity: ['Mild', 'Moderate', 'Severe'],
  location: ['Left side', 'Right side', 'Central', 'Widespread'],
};

const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English', flag: 'EN' },
  { code: 'hi', label: 'Hindi',   native: 'हिन्दी',  flag: 'HI' },
  { code: 'or', label: 'Odia',    native: 'ଓଡ଼ିଆ',   flag: 'OR' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা', flag: 'BN' },
  { code: 'ta', label: 'Tamil',   native: 'தமிழ்', flag: 'TA' },
  { code: 'te', label: 'Telugu',  native: 'తెలుగు', flag: 'TE' },
  { code: 'mr', label: 'Marathi', native: 'मराठी', flag: 'MR' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ', flag: 'KN' },
];

// ─── Triage modes ─────────────────────────────────────────────────────────────
// Both endpoints take the same ChatRequest and return the same ChatResponse,
// so only the endpoint differs.
const TRIAGE_MODES = {
  quick: {
    label: 'Quick Triage', tag: 'Quick', endpoint: '/chat',
    hint: 'Answer a few short questions to find out how soon you should see a doctor.',
  },
  detailed: {
    label: 'Detailed Triage', tag: 'Detailed', endpoint: '/chat/detailed',
    hint: 'Go through your symptoms and history step by step for a fuller assessment.',
  },
};

// History doesn't reliably say which mode a session used, so remember it locally
// for sessions started in this browser (keyed by sessionid).
const MODE_MAP_KEY = 'sym_checker_session_modes';

function rememberSessionMode(sid, mode) {
  try {
    const map = JSON.parse(localStorage.getItem(MODE_MAP_KEY) || '{}');
    map[sid] = mode;
    localStorage.setItem(MODE_MAP_KEY, JSON.stringify(map));
  } catch {}
}

function sessionModeOf(raw, sid) {
  const fromApi = String(raw?.mode || raw?.triage_type || raw?.chat_type || '').toLowerCase();
  if (fromApi.includes('detail')) return 'detailed';
  if (fromApi.includes('quick')) return 'quick';
  try { return JSON.parse(localStorage.getItem(MODE_MAP_KEY) || '{}')[sid] || null; } catch { return null; }
}

// ─── Session persistence helpers ─────────────────────────────────────────────
const SESSION_KEY = 'sym_checker_session';

function saveSessionToStorage(data) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch {}
}

function loadSessionFromStorage() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
}

function clearSessionFromStorage() {
  try { sessionStorage.removeItem(SESSION_KEY); } catch {}
}

// ─── History grouping helper ──────────────────────────────────────────────────
function groupSessionsByDate(sessions) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);

  const groups = { Today: [], Yesterday: [], Previous: [] };

  sessions.forEach(s => {
    const d = s.date ? new Date(s.date) : null;
    if (!d || isNaN(d)) { groups.Previous.push(s); return; }
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day >= today)           groups.Today.push(s);
    else if (day >= yesterday)  groups.Yesterday.push(s);
    else                        groups.Previous.push(s);
  });

  return groups;
}

function formatHistoryDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric' });
}

function extractChips(question) {
  const q = (question || '').toLowerCase();
  if (q.includes('nature') || q.includes('type of') || q.includes('kind of')) return CHIP_PATTERNS.nature;
  if (q.includes('duration') || q.includes('how long') || q.includes('since when')) return CHIP_PATTERNS.duration;
  if (q.includes('severe') || q.includes('intensity') || q.includes('scale')) return CHIP_PATTERNS.severity;
  if (q.includes('location') || q.includes('where')) return CHIP_PATTERNS.location;
  return [];
}

// FIX K: getTime() captures the time at call-time (message creation),
// not at render time. Always call this when building the message object,
// never inside the render path as a fallback.
function getTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── useIsMobile hook ─────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ─── Lock body scroll on mobile when chat is active ──────────────────────────
// Height of the area actually visible on screen. On mobile this shrinks when the
// keyboard opens (100vh does not), so the chat can be sized to fit above it.
function useVisibleHeight(enabled) {
  const read = () => Math.round(window.visualViewport?.height || window.innerHeight);
  const [height, setHeight] = useState(read);
  useEffect(() => {
    if (!enabled) return undefined;
    const vv = window.visualViewport;
    const update = () => {
      setHeight(read());
      // iOS scrolls the (fixed) page to reveal the focused input — pin it back,
      // otherwise the chat shifts up and its scroll area can't be reached.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled]);
  return height;
}

function useBodyScrollLock(lock) {
  useEffect(() => {
    if (!lock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width    = '100%';
    return () => {
      document.body.style.overflow = prev;
      document.body.style.position = '';
      document.body.style.width    = '';
    };
  }, [lock]);
}

// ─── Bot Avatar ───────────────────────────────────────────────────────────────
function BotAvatar() {
  return (
    <img
      src={internalPageImge}
      alt="bot"
      style={{ width: 36, height: 36, objectFit: 'contain', display: 'block', flexShrink: 0 }}
      onError={e => { e.currentTarget.src = patientChatImg; }}
    />
  );
}

// ─── Thinking bubble ──────────────────────────────────────────────────────────
function ThinkingBubble() {
  // FIX K: capture time once on mount, not on every render
  const timeRef = useRef(getTime());
  return (
    <div style={s.msgRow}>
      <BotAvatar />
      <div style={s.msgCol}>
        <div style={{ ...s.bubble, ...s.bubbleBot }}>
          <div style={s.analyzingText}>Analyzing Severity of your symptoms...</div>
          <div style={s.thinkingDots}>
            {[0, 1, 2].map(i => <span key={i} style={{ ...s.dot, animationDelay: `${i * 0.2}s` }} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TTS Button ───────────────────────────────────────────────────────────────
// sharedAudioRef: a ref owned by the parent (SymptomCheckerChat) that always
// points to the currently-playing Audio object so startNewChat can stop it.
function TTSButton({ text, lang, sharedAudioRef, sharedSetPlayingRef }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef              = useRef(null);

  const handlePlay = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      if (sharedAudioRef) sharedAudioRef.current = null;
      if (sharedSetPlayingRef) sharedSetPlayingRef.current = null;
      setPlaying(false);
      return;
    }

    // Stop whichever TTSButton was playing before this one and reset its UI
    if (sharedAudioRef?.current && sharedAudioRef.current !== audioRef.current) {
      sharedAudioRef.current.pause();
      sharedAudioRef.current = null;
      if (sharedSetPlayingRef?.current) {
        sharedSetPlayingRef.current(false);
        sharedSetPlayingRef.current = null;
      }
    }

    // Register this button's setter as the active one
    if (sharedSetPlayingRef) sharedSetPlayingRef.current = setPlaying;

    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('text', text);
      formData.append('language', lang || 'en');

      const res = await fetch(`${BASE}/proxy/text-to-speech`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (!res.ok) throw new Error('TTS failed');

      const blob  = URL.createObjectURL(await res.blob());
      const audio = new Audio(blob);
      audioRef.current = audio;
      if (sharedAudioRef) sharedAudioRef.current = audio;

      audio.onended = () => {
        setPlaying(false);
        URL.revokeObjectURL(blob);
        if (sharedAudioRef && sharedAudioRef.current === audio) sharedAudioRef.current = null;
      };
      audio.onerror = () => {
        setPlaying(false);
        setLoading(false);
        if (sharedAudioRef && sharedAudioRef.current === audio) sharedAudioRef.current = null;
      };

      await audio.play();
      setPlaying(true);
    } catch (e) {
      console.error('TTS error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (sharedAudioRef && sharedAudioRef.current === audioRef.current) sharedAudioRef.current = null;
    }
  }, []);
  

  return (
    <button
      style={{
        ...s.ttsBtn,
        color: playing ? 'var(--color-brand-primary)' : '#aaa',
        opacity: loading ? 0.6 : 1,
      }}
      onClick={handlePlay}
      disabled={loading}
      title={playing ? 'Stop audio' : 'Listen to this message'}
    >
      {loading ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ animation: 'scb-spin 0.8s linear infinite' }}>
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
      ) : playing ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="6" width="12" height="12" rx="2"/>
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
        </svg>
      )}
    </button>
  );
}

// ─── Bot message ──────────────────────────────────────────────────────────────
function BotMessage({ text, chips, onChipClick, time, isReadOnly, lang, sharedAudioRef, sharedSetPlayingRef }) {
  const isMobile = useIsMobile();
  return (
    <div style={s.msgRow}>
      <BotAvatar />
      <div style={{ ...s.msgCol, maxWidth: isMobile ? '85%' : '68%' }}>
        <div style={{ ...s.bubble, ...s.bubbleBot }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ flex: 1 }}>{text}</span>
            <TTSButton text={text} lang={lang} sharedAudioRef={sharedAudioRef} sharedSetPlayingRef={sharedSetPlayingRef} />
          </div>
        </div>
        {!isReadOnly && chips && chips.length > 0 && (
          <div style={s.chips}>
            {chips.map(c => (
              <button key={c} style={s.chip}
                onClick={() => onChipClick(c)}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--primary-gradient-180)';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.color = 'var(--color-brand-primary)';
                  e.currentTarget.style.borderColor = 'var(--color-brand-primary)';
                }}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Result-card text helpers ─────────────────────────────────────────────────
// Facility addresses carry a 2-letter state code as one comma segment ("…, OD, 751013");
// show the full state name instead. Only an exact code segment is replaced.
const STATE_NAMES = {
  AN: 'Andaman and Nicobar Islands', AP: 'Andhra Pradesh', AR: 'Arunachal Pradesh', AS: 'Assam',
  BR: 'Bihar', CH: 'Chandigarh', CG: 'Chhattisgarh', CT: 'Chhattisgarh', DL: 'Delhi', GA: 'Goa',
  GJ: 'Gujarat', HR: 'Haryana', HP: 'Himachal Pradesh', JK: 'Jammu and Kashmir', JH: 'Jharkhand',
  KA: 'Karnataka', KL: 'Kerala', LA: 'Ladakh', LD: 'Lakshadweep', MP: 'Madhya Pradesh',
  MH: 'Maharashtra', MN: 'Manipur', ML: 'Meghalaya', MZ: 'Mizoram', NL: 'Nagaland',
  OD: 'Odisha', OR: 'Odisha', PY: 'Puducherry', PB: 'Punjab', RJ: 'Rajasthan', SK: 'Sikkim',
  TN: 'Tamil Nadu', TS: 'Telangana', TG: 'Telangana', TR: 'Tripura', UP: 'Uttar Pradesh',
  UK: 'Uttarakhand', UT: 'Uttarakhand', WB: 'West Bengal',
  DN: 'Dadra and Nagar Haveli and Daman and Diu', DD: 'Dadra and Nagar Haveli and Daman and Diu',
};
const formatFacilityAddress = (address = '') =>
  String(address)
    .split(',')
    .map((part) => {
      const code = part.trim();
      return /^[A-Z]{2}$/.test(code) && STATE_NAMES[code] ? part.replace(code, STATE_NAMES[code]) : part;
    })
    .join(',');

// Bold the recommended specialty in the text. The API label is often the department
// ("Gastroenterology", "Psychiatry", "Pediatrics") while the sentence names the doctor
// ("Gastroenterologist", "Psychiatrist", "Pediatrician"), so single-word labels match on
// their stem; multi-word labels ("General Physician") match as an exact phrase.
const specialtyRegex = (label = '') => {
  const t = label.trim();
  if (!t) return null;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (/\s/.test(t) || t.length < 6) return new RegExp(`(${esc(t)})`, 'gi');
  const stem = t.replace(/ics$/i, '').replace(/y$/i, '').replace(/s$/i, '');
  return new RegExp(`(\\b${esc(stem)}\\w*)`, 'gi');
};
const boldTerm = (text, term) => {
  const re = text && specialtyRegex(term);
  if (!re) return text;
  // split() with a capture group puts the matches at the odd indexes
  return text.split(re).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
};

// ─── User message ─────────────────────────────────────────────────────────────
function UserMessage({ text, time }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ ...s.msgRow, flexDirection: 'row-reverse' }}>
      <div style={{ ...s.msgColUser, maxWidth: isMobile ? '85%' : '68%' }}>
        <div style={{ ...s.bubble, ...s.bubbleUser }}>
          <span style={{ flex: 1 }}>{text}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────
function ResultCard({ data, time, lang }) {
  const isMobile = useIsMobile();
  const [coupon, setCoupon] = useState(null);
  const [couponCopied, setCouponCopied] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(COUPON_API_URL)
      .then((res) => res.json())
      .then((json) => { if (active && json?.valid) setCoupon(json); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const handleCopyCoupon = () => {
    if (!coupon?.couponCode) return;
    navigator.clipboard?.writeText(coupon.couponCode).then(() => {
      setCouponCopied(true);
      setTimeout(() => setCouponCopied(false), 2000);
    });
  };

  const lvl      = (data.emergency_level || 'medium').toLowerCase();
  const pctMap   = { low: 20, medium: 55, high: 80, critical: 95 };
  const pct      = pctMap[lvl] || 55;
  const lvlLabel = lvl.charAt(0).toUpperCase() + lvl.slice(1);
  const lvlColor = { low: '#22c55e', medium: '#f97316', high: '#ef4444', critical: '#991b1b' }[lvl] || '#f97316';

  const diagnoses     = data.diagnoses || [];
  const homeRemedies  = data.home_remedies || [];
  const allFacilities = [...(data.nearby_hospitals || []), ...(data.nearby_clinics || [])];
  const rec           = data.recommendation || '';
  const summary       = data.summary || '';
  const specialty     = data.doctor_specialty?.label || 'a specialist';
  const diagColors    = ['#dc2626', '#7c3aed', '#475569', '#0ea5e9'];

  const ttsText = [
    summary && `Summary: ${summary}`,
    `Severity: ${lvlLabel}.`,
    diagnoses.length > 0 && `Possible conditions: ${diagnoses.map(d => d.name).join(', ')}.`,
    rec || `Consider seeing a ${specialty}.`,
  ].filter(Boolean).join(' ');

  return (
    <div style={{ ...s.msgRow }}>
      <BotAvatar />
      <div style={{ ...s.msgCol, maxWidth: isMobile ? '92%' : '90%' }}>
        <div style={s.resultCard}>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>Assessment Result</span>
          </div>

          {summary ? (
            <div style={s.summaryBox}>
              <span style={s.summaryLabel}>📋 Summary</span>
              <p style={s.summaryText}>{summary}</p>
            </div>
          ) : null}

          <div style={s.sectionTitle}>Case Severity</div>
          <div style={{ marginBottom: 20 }}>
            <div style={s.severityTrack}>
              <div style={{ ...s.severityThumb, left: `${pct}%` }} />
            </div>
            <div style={s.severityLabels}>
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
              <span>Critical</span>
            </div>
          </div>

          {diagnoses.length > 0 && (
            <>
              <div style={s.sectionTitle}>Analysis</div>
              <div style={{ ...s.diagnosisGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                {diagnoses.map((d, i) => {
                  const pctVal = Math.round(d.probability > 1 ? d.probability : d.probability * 100);
                  const color  = diagColors[i % diagColors.length];
                  return (
                    <div key={i} style={s.diagItem}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{d.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color }}>{pctVal}%</span>
                      </div>
                      <div style={s.diagBarTrack}>
                        <div style={{ ...s.diagBarFill, width: `${pctVal}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ ...s.recRow, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>👨‍⚕️</span>
              <span style={s.recText}>
                {boldTerm(rec || `It's not urgent, but consider seeing a ${specialty}.`, data.doctor_specialty?.label)}
              </span>
            </div>
            <button style={{ ...s.bookBtn, alignSelf: isMobile ? 'stretch' : 'auto', textAlign: 'center' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              onClick={() => {
                const specialityLabel = data.doctor_specialty?.label || '';
                // const specialityCode = data.doctor_specialty?.code || '';
                const today = new Date().toISOString().split('T')[0];
                const params = new URLSearchParams({
                  speciality: specialityLabel,
                  date: today,
                  consultationType: 'V',
                  feeMin: '0',
                  feeMax: '2000',
                  pageNumber: '0',
                  pageSize: '10',
                });
                window.open(`${process.env.REACT_APP_NEW_DOCTOR_URL}doctor-detail?${params.toString()}`, '_blank');
              }}>
              Book Consultation
            </button>
          </div>

          {coupon && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              margin: '12px 0 0 0',
              padding: '10px 16px',
              borderRadius: 999,
              textAlign: 'center',
              background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)',
              border: '1px solid #00B5E2',
            }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: 'var(--color-brand-primary)' }}>
                Use Coupon Code
              </span>
              <span
                onClick={handleCopyCoupon}
                title="Click to copy"
                style={{
                  fontFamily: 'Inter, monospace',
                  fontWeight: 700,
                  fontSize: 13,
                  color: 'var(--color-brand-green)',
                  background: '#ffffff',
                  border: '1.5px dashed #00B5E2',
                  borderRadius: 8,
                  padding: '2px 10px',
                  cursor: 'pointer',
                }}>
                {couponCopied ? 'Copied!' : coupon.couponCode}
              </span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: 'var(--color-brand-primary)' }}>
                to get 100% OFF on Doctor Consultation !
              </span>
            </div>
          )}

          {homeRemedies.length > 0 && (
            <>
              <div style={{ ...s.sectionTitle, marginTop: 20 }}>🏠 Home Remedies</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
                {homeRemedies.map((remedy, i) => (
                  <div key={i} style={s.remedyItem}>
                    <div style={s.remedyDot}>{i + 1}</div>
                    <span style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{remedy}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#f97316', fontStyle: 'italic', marginBottom: 8 }}>
                ⚠️ General suggestions only. Consult a doctor if symptoms worsen.
              </div>
            </>
          )}

          {allFacilities.length > 0 && (
            <>
              <div style={{ ...s.sectionTitle, marginTop: 16 }}>🏥 Nearby Facilities</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {allFacilities.map((h, i) => (
                  <div key={i} style={s.hospitalItem}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>
                      {h.hospital_name || h.clinic_name || 'Health Facility'}
                    </div>
                    {h.clinic_name && h.hospital_name && (
                      <div style={{ fontSize: 12, color: 'var(--color-brand-primary)', marginTop: 2 }}>{h.clinic_name}</div>
                    )}
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4, lineHeight: 1.5 }}>📍 {formatFacilityAddress(h.address)}</div>
                    {h.contact_number && (
                      <a href={`tel:${h.contact_number}`}
                        style={{ fontSize: 12, color: '#22c55e', marginTop: 4, display: 'block', textDecoration: 'none' }}>
                        📞 {h.contact_number}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Mic Button ───────────────────────────────────────────────────────────────
function MicButton({ onTranscript, onError, disabled, lang }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef          = useRef(null);
  const chunksRef                 = useRef([]);

  const startRecording = async () => {
    if (recording || uploading || disabled) return;
    try {
      const stream        = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('audio_file', blob, 'recording.webm');
          formData.append('src_lang', lang || 'en');

          const res = await fetch(`${BASE}/proxy/speech-to-text`, {
            method: 'POST',
            body: formData,
          });
          if (!res.ok) throw new Error('STT failed');

          const data = await res.json();
          const transcript =
            data.recognized_text ||
            data.transcript      ||
            data.text            ||
            data.result          ||
            (typeof data === 'string' ? data : '');

          if (transcript) {
            onTranscript(transcript.trim());
          } else {
            onError("Could not hear clearly. Please speak again 🎤");
          }
        } catch (e) {
          console.error('STT error:', e);
          onError("Could not understand. Please speak clearly and try again 🎤");
        } finally {
          setUploading(false);
        }
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (e) {
      console.error('Mic access error:', e);
      onError('Microphone access denied. Please allow microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const isActive = recording || uploading;

  return (
    <button
      style={{
        ...s.micBtn,
        background: recording
          ? 'var(--primary-gradient-180)'
          : uploading
            ? 'rgba(0,66,118,0.12)'
            : 'transparent',
        border: recording ? '2px solid var(--color-brand-primary)' : '2px solid rgba(0,66,118,0.25)',
        color: isActive ? '#fff' : 'var(--color-brand-primary)',
        opacity: disabled && !recording ? 0.4 : 1,
        boxShadow: recording ? '0 0 0 4px rgba(0,66,118,0.18)' : 'none',
      }}
      onClick={recording ? stopRecording : startRecording}
      disabled={uploading || (disabled && !recording)}
      title={recording ? 'Stop recording' : uploading ? 'Transcribing...' : 'Speak your symptom'}
    >
      {uploading ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ animation: 'scb-spin 0.8s linear infinite' }}>
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
      ) : recording ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"
          style={{ animation: 'scb-pulse 1s ease-in-out infinite' }}>
          <rect x="6" y="6" width="12" height="12" rx="2"/>
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      )}
    </button>
  );
}

// ─── Language Selector Modal ──────────────────────────────────────────────────
function LanguageModal({ onSelect }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div style={s.modalOverlay}>
      <div style={s.modalCard}>
        <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>🌐</div>
        <h2 style={s.modalTitle}>Choose Your Language</h2>
        <p style={s.modalSub}>Select the language for voice input &amp; audio responses</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
          {LANGUAGES.map(({ code, label, native, flag }) => (
            <button
              key={code}
              style={{
                ...s.langBtn,
                background: hovered === code
                  ? 'var(--primary-gradient-180)'
                  : '#fff',
                color: hovered === code ? '#fff' : 'var(--color-brand-primary)',
                borderColor: hovered === code ? 'transparent' : 'var(--color-brand-primary)',
                transform: hovered === code ? 'scale(1.02)' : 'scale(1)',
              }}
              onMouseEnter={() => setHovered(code)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelect(code)}
            >
              <span style={{ fontSize: 22 }}>{flag}</span>
              <span style={{ flex: 1, textAlign: 'left', fontSize: 15, fontWeight: 600 }}>{label}</span>
              <span style={{ fontSize: 15, opacity: hovered === code ? 0.9 : 0.7, fontWeight: 500 }}>{native}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Quick / Detailed triage toggle ──────────────────────────────────────────
const QuickIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/></svg>
);
const DetailedIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8"/>
  </svg>
);

function TriageToggle({ mode, onChange, isMobile }) {
  const [tip, setTip] = useState(null);
  return (
    <div style={s.triageWrap}>
      <div style={s.triageLabel}>Choose how you'd like to check</div>
      <div style={{ ...s.triageTrack, width: isMobile ? '100%' : 340 }} role="radiogroup" aria-label="Triage type">
        {Object.entries(TRIAGE_MODES).map(([key, m]) => {
          const on = mode === key;
          return (
            <div
              key={key}
              style={{ position: 'relative', flex: 1 }}
              onMouseEnter={() => !isMobile && setTip(key)}
              onMouseLeave={() => setTip(null)}
            >
              <button
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onChange(key)}
                onFocus={() => !isMobile && setTip(key)}
                onBlur={() => setTip(null)}
                style={{ ...s.triageBtn, ...(on ? s.triageBtnOn : {}), fontSize: isMobile ? 12 : 14 }}
              >
                <span style={{ ...s.triageIcon, ...(on ? s.triageIconOn : {}) }}>
                  {key === 'quick' ? <QuickIcon /> : <DetailedIcon />}
                </span>
                {m.label}
              </button>
              {tip === key && <div style={s.triageTip} role="tooltip">{m.hint}</div>}
            </div>
          );
        })}
      </div>
      {/* No hover on touch screens — show the selected mode's hint instead */}
      {isMobile && <div style={s.triageHintMobile}>{TRIAGE_MODES[mode].hint}</div>}
    </div>
  );
}

// ─── History Search + Grouped Sidebar ────────────────────────────────────────
function HistoryPanel({
  sessions, historyLoading, historyOpen, setHistoryOpen,
  fetchHistory, loadingSession, loadSession,
}) {
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? sessions.filter(s =>
        s.label.toLowerCase().includes(search.toLowerCase()) ||
        (s.date && formatHistoryDate(s.date).toLowerCase().includes(search.toLowerCase()))
      )
    : sessions;

  const groups = groupSessionsByDate(filtered);

  const renderGroup = (label, items) => {
    if (!items.length) return null;
    return (
      <div key={label}>
        <div style={s.historyGroupLabel2}>{label}</div>
        {items.map(ss => (
          <button
            key={ss.id}
            style={{
              ...s.historyItem,
              ...(ss.active ? s.historyItemActive : {}),
              opacity: loadingSession === ss.id ? 0.6 : 1,
            }}
            onClick={() => loadSession(ss)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <span style={{ flex: 1, fontWeight: ss.active ? 600 : 400, fontSize: 13, color: ss.active ? 'var(--color-brand-primary)' : '#333' }}>
                  {loadingSession === ss.id ? '⏳ ' : ''}{ss.label}
                </span>
                {ss.mode && <span style={s.historyModeTag}>{TRIAGE_MODES[ss.mode].tag}</span>}
              </span>
              {ss.date && (
                <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>
                  {formatHistoryDate(ss.date)}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div style={s.historySection}>
      <div style={s.historySpacer} />

      <button style={s.historyGroupHeader} onClick={() => {
        setHistoryOpen(v => !v);
        fetchHistory();
      }}>
        <span style={s.historyGroupLabelTitle}>Chat History</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#004276" strokeWidth="2.5"
          style={{ transform: historyOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {historyOpen && (
        <>
          <div style={s.searchWrap}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#004276" strokeWidth="2.5" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              style={s.searchInput}
              placeholder="Search history..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#bbb', display: 'flex' }}
                onClick={() => setSearch('')}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>

          {historyLoading && <div style={s.historyEmpty}>Loading...</div>}
          {!historyLoading && sessions.length === 0 && <div style={s.historyEmpty}>No past sessions</div>}
          {!historyLoading && sessions.length > 0 && filtered.length === 0 && (
            <div style={s.historyEmpty}>No results for "{search}"</div>
          )}

          {renderGroup('Today', groups.Today)}
          {renderGroup('Yesterday', groups.Yesterday)}
          {renderGroup('Previous', groups.Previous)}
        </>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const SymptomCheckerChat = () => {
  const storedData = (() => {
    try { return JSON.parse(localStorage.getItem('loginObj') || '{}'); }
    catch { return {}; }
  })();

  const patientId   = storedData?.code || '';
  const patientName = storedData?.patientName || storedData?.name || 'there';
  // Real PIN comes from the patient profile (fetched below); loginObj rarely has one,
  // and 751001 is only a last-resort default so nearby facilities still load
  const [profilePincode, setProfilePincode] = useState(null);
  const pincode     = profilePincode || storedData?.pinCode || storedData?.pincode || '751001';

  const isMobile = useIsMobile();

  useBodyScrollLock(isMobile);
  const visibleHeight = useVisibleHeight(isMobile);

  const [selectedLang, setSelectedLang]         = useState(null);
  const [sessionLang, setSessionLang]           = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [chatStarted, setChatStarted]           = useState(false);
  const [sessionId, setSessionId]               = useState(null);
  const [messages, setMessages]                 = useState([]);
  const [inputValue, setInputValue]             = useState('');
  const [isLoading, setIsLoading]               = useState(false);
  const [isReadOnly, setIsReadOnly]             = useState(false);
  const [sessions, setSessions]                 = useState([]);
  const [historyLoading, setHistoryLoading]     = useState(false);
  const [loadingSession, setLoadingSession]     = useState(null);
  const [historyOpen, setHistoryOpen]           = useState(true);
  const [toast, setToast]                       = useState(null);
  const [triageMode, setTriageMode]             = useState('quick');
  // Mode of a past session opened from history (null when unknown)
  const [viewedMode, setViewedMode]             = useState(null);

  // FIX M: track whether the latest appended message is a result card
  const lastMsgTypeRef = useRef(null);

  const activeTTSLang = isReadOnly && sessionLang ? sessionLang : selectedLang;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const chatAreaRef   = useRef(null);
  const inputRef      = useRef(null);
  const activeTTSRef          = useRef(null); // tracks the currently-playing TTS audio
  const activeTTSSetPlayingRef = useRef(null); // tracks setPlaying of the currently-playing TTSButton
  const [profileAge, setProfileAge]       = useState(null);
const [profileGender, setProfileGender] = useState(null);

useEffect(() => {
  const fetchProfile = async () => {
    if (!patientId) return;
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(`${process.env.REACT_APP_BASEURL}patient/${patientId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const json = await res.json();
      const data = json?.data || json;

      if (data?.dob) {
        const birth = new Date(data.dob);
        const age   = Math.floor((new Date() - birth) / (365.25 * 24 * 60 * 60 * 1000));
        setProfileAge(age);
      }
      if (data?.gender) setProfileGender(data.gender);

      // Prefer the default address-book PIN, then the profile's own PIN; ignore blanks/invalid values
      const isValidPin = (p) => /^\d{6}$/.test(String(p ?? '').trim());
      const pin = [data?.addressBookPincode, data?.pinCode].find(isValidPin);
      if (pin) setProfilePincode(String(pin).trim());
    } catch (e) {
      console.error('Profile fetch failed:', e);
    }
  };
  fetchProfile();
}, [patientId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') {
      clearSessionFromStorage();
      window.history.replaceState({}, '', '/symptomchecker/chat');
      return;
    }
    const saved = loadSessionFromStorage();
    if (saved) {
      setSelectedLang(saved.selectedLang || 'en');
      setSessionId(saved.sessionId || null);
      setMessages(saved.messages || []);
      setChatStarted(saved.chatStarted || false);
      setIsReadOnly(saved.isReadOnly || false);
      setSessionLang(saved.sessionLang || null);
      setTriageMode(saved.triageMode === 'detailed' ? 'detailed' : 'quick');
      setViewedMode(saved.viewedMode || null);
    }
  }, []);

  useEffect(() => {
    if (!selectedLang) return;
    saveSessionToStorage({ selectedLang, sessionId, messages, chatStarted, isReadOnly, sessionLang, triageMode, viewedMode });
  }, [selectedLang, sessionId, messages, chatStarted, isReadOnly, sessionLang, triageMode, viewedMode]);

  // FIX M: scroll to bottom for normal messages; scroll result card into view at its TOP
  useEffect(() => {
    if (!chatAreaRef.current) return;
    if (lastMsgTypeRef.current === 'result') {
      // Bring the result card's top into view by scrolling ONLY the chat area.
      // (scrollIntoView also scrolls the page, which pushed the top of the chat under the
      // fixed header — and with the page scroll locked on mobile, it couldn't be scrolled back.)
      const area = chatAreaRef.current;
      const lastChild = area.lastElementChild;
      if (lastChild) {
        const top = lastChild.getBoundingClientRect().top - area.getBoundingClientRect().top + area.scrollTop;
        area.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Keyboard opened/closed while typing → keep the latest message visible above it.
  useEffect(() => {
    if (!isMobile || !chatAreaRef.current) return;
    if (document.activeElement === inputRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [visibleHeight, isMobile]);

  useEffect(() => {
    if (patientId) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    if (!isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  // Keep the caret in the chat input whenever it's enabled, instead of
  // requiring the user to click into it after every bot reply.
  useEffect(() => {
    if (!isLoading && !isReadOnly) {
      inputRef.current?.focus();
    }
  }, [isLoading, isReadOnly, messages]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res  = await fetch(`${BASE}/history/${patientId}?status=COMPLETED`);
      const data = await res.json();
      if (Array.isArray(data.sessions)) {
        setSessions(data.sessions.map(s => ({
          id: s.sessionid, label: s.first_message || 'Symptom Check',
          status: s.status || '', active: false, date: s.started_at || '', raw: s,
          mode: sessionModeOf(s, s.sessionid),
        })));
      }
    } catch (e) { console.error('History fetch failed:', e); }
    finally { setHistoryLoading(false); }
  };

  const loadSession = async (session) => {
    if (loadingSession) return;
    setLoadingSession(session.id);
    setChatStarted(true);
    setIsReadOnly(true);
    setSessionId(session.id);
    setViewedMode(session.mode || null);
    setMessages([]);
    setSessions(prev => prev.map(s => ({ ...s, active: s.id === session.id })));
    if (isMobile) setMobileSidebarOpen(false);

    let data = session.raw;

    if (!data || (!data.conversation && !data.diagnoses && !data.summary)) {
      try {
        const res     = await fetch(`${BASE}/history/${patientId}?status=COMPLETED`);
        const fetched = await res.json();
        if (Array.isArray(fetched.sessions)) {
          const refreshed = fetched.sessions.map(s => ({
            id: s.sessionid, label: s.first_message || 'Symptom Check',
            status: s.status || '', active: false, date: s.started_at || '', raw: s,
            mode: sessionModeOf(s, s.sessionid),
          }));
          setSessions(refreshed.map(s => ({ ...s, active: s.id === session.id })));
          const found = refreshed.find(s => s.id === session.id);
          if (found) data = found.raw;
        }
      } catch (e) { console.error('Session refresh failed:', e); }
    }

    if (!data) {
      setMessages([{ type: 'bot', text: 'Session data not available.', chips: [], time: getTime() }]);
      setLoadingSession(null);
      return;
    }

    const restoredLang = data.language || data.lang || 'en';
    setSessionLang(restoredLang);

    const chats   = data.conversation || [];
    const rebuilt = [];
    const timeStr = data.started_at
      ? new Date(data.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : getTime();

    chats.forEach(c => {
      if (c.role === 'USER')        rebuilt.push({ type: 'user', text: c.message, time: timeStr });
      else if (c.role === 'SYSTEM') rebuilt.push({ type: 'bot',  text: c.message, chips: [], time: timeStr });
    });
    if (data.diagnoses || data.summary) rebuilt.push({ type: 'result', data, time: timeStr });

    // FIX M: mark last message type before setting messages
    if (rebuilt.length > 0) {
      lastMsgTypeRef.current = rebuilt[rebuilt.length - 1].type;
    }
    setMessages(rebuilt);
    setLoadingSession(null);
  };

  const createSession = async () => {
    // FIX L: stamp `date` with current ISO timestamp so the new session
    // lands in "Today" instead of "Previous" (which happens when date is absent).
    const nowISO = new Date().toISOString();
    try {
      const res  = await fetch(`${BASE}/session_create`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
    patient_id: patientId,   // ← note: patient_id
    source: 'd2c',           // ← add this
  }), });
      const data = await res.json();
      const sid  = data.sessionid;
      setSessionId(sid);
      rememberSessionMode(sid, triageMode);
      setSessions(prev => [
        { id: sid, label: 'New Conversation', status: 'IN_PROGRESS', active: true, date: nowISO, mode: triageMode },
        ...prev.map(s => ({ ...s, active: false })),
      ]);
      return sid;
    } catch {
      const sid = 'local-' + Date.now();
      setSessionId(sid);
      setSessions(prev => [
        { id: sid, label: 'New Conversation', status: 'IN_PROGRESS', active: true, date: nowISO, mode: triageMode },
        ...prev.map(s => ({ ...s, active: false })),
      ]);
      return sid;
    }
  };

  const startNewChat = () => {
    // Stop any TTS audio that is currently playing
    if (activeTTSRef.current) { activeTTSRef.current.pause(); activeTTSRef.current = null; }
    if (activeTTSSetPlayingRef.current) { activeTTSSetPlayingRef.current(false); activeTTSSetPlayingRef.current = null; }
    setChatStarted(false); setSessionId(null); setMessages([]);
    setInputValue(''); setIsReadOnly(false);
    setSessionLang(null);
    setViewedMode(null);
    lastMsgTypeRef.current = null;
    setSessions(prev => prev.map(s => ({ ...s, active: false })));
    clearSessionFromStorage();
    if (isMobile) setMobileSidebarOpen(false);
  };

  const sendMessage = async (text) => {
    const msg = (text || inputValue).trim();
    if (!msg || isLoading || isReadOnly) return;
    setInputValue('');
    if (!chatStarted) setChatStarted(true);

    let sid = sessionId;
    if (!sid) sid = await createSession();

    // FIX K: stamp the time exactly when the user message is created
    const userTime = getTime();
    lastMsgTypeRef.current = 'user';
    setMessages(prev => [...prev, { type: 'user', text: msg, time: userTime }]);
    setIsLoading(true);
    lastMsgTypeRef.current = 'thinking';
    setMessages(prev => [...prev, { type: 'thinking' }]);

    try {
      const res = await fetch(`${BASE}${TRIAGE_MODES[triageMode].endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientid: patientId,
          question: msg,
          sessionid: sid,
          source: 'd2c',
          pincode,
          language: selectedLang || 'en',
            profile_age: profileAge,
  profile_gender: profileGender,
        }),
      });
      const data = await res.json();
      setMessages(prev => prev.filter(m => m.type !== 'thinking'));

      if (data.next_question) {
        // FIX K: stamp time when the bot reply arrives
        const botTime = getTime();
        lastMsgTypeRef.current = 'bot';
        setMessages(prev => [...prev, { type: 'bot', text: data.next_question, chips: extractChips(data.next_question), time: botTime }]);
      } else {
        const resultTime = getTime();
        lastMsgTypeRef.current = 'result';
        setMessages(prev => [...prev, { type: 'result', data, time: resultTime }]);
        setSessions(prev => prev.map(s => s.id === sid ? { ...s, label: 'Symptom Check', status: 'COMPLETED' } : s));
        // Chat is complete — disable input so user can't type after the result
        setViewedMode(triageMode);
        setIsReadOnly(true);
        fetchHistory();
      }
    } catch {
      setMessages(prev => prev.filter(m => m.type !== 'thinking'));
      const errTime = getTime();
      lastMsgTypeRef.current = 'bot';
      setMessages(prev => [...prev, { type: 'bot', text: 'Something went wrong. Please try again.', chips: [], time: errTime }]);
    }
    setIsLoading(false);
    inputRef.current?.focus();
  };

  // Each recording is appended to what's already typed/spoken, so users can add details they missed
  const handleTranscript = (transcript) => {
    setInputValue((prev) => {
      const existing = prev.trimEnd();
      return existing ? `${existing} ${transcript}` : transcript;
    });
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const activeLang = LANGUAGES.find(l => l.code === selectedLang) || LANGUAGES[0];

  if (!selectedLang) {
    return <LanguageModal onSelect={setSelectedLang} />;
  }

  // ── Sidebar content ────────────────────────────────────────────────────────
  const SidebarContent = () => (
    <>
      {!isMobile && (
        <button style={s.sidebarToggle} onClick={() => setSidebarCollapsed(v => !v)} title="Toggle sidebar">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#004276" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/>
          </svg>
        </button>
      )}

      {isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-brand-primary)' }}>Menu</span>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            onClick={() => setMobileSidebarOpen(false)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#004276" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {(!isMobile && sidebarCollapsed) ? (
        <button style={s.newChatBtnCollapsed} onClick={startNewChat} title="New Chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
      ) : (
        <button style={s.newChatBtn} onClick={startNewChat}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
          <span>New Chat</span>
        </button>
      )}

      {(isMobile || !sidebarCollapsed) && (
        <HistoryPanel
          sessions={sessions}
          historyLoading={historyLoading}
          historyOpen={historyOpen}
          setHistoryOpen={setHistoryOpen}
          fetchHistory={fetchHistory}
          loadingSession={loadingSession}
          loadSession={loadSession}
        />
      )}
    </>
  );

  return (
    <>
      <div style={isMobile ? { ...s.wrapper, height: visibleHeight - HEADER_HEIGHT, maxHeight: 'none' } : s.wrapper}>
        <Header />
        {isMobile && mobileSidebarOpen && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 98, background: 'rgba(0,0,0,0.35)' }}
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {!isMobile && (
          <div style={{ ...s.sidebar, width: sidebarCollapsed ? 64 : 260 }}>
            <SidebarContent />
          </div>
        )}

        {isMobile && (
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0,
            width: 280, zIndex: 99,
            background: '#fff',
            boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
            display: 'flex', flexDirection: 'column',
            padding: '16px 14px', gap: 10,
            transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
            overflowY: 'auto',
          }}>
            <SidebarContent />
          </div>
        )}

        {/* ── Main ── */}
        <div style={s.main}>

          {/* Mobile top bar */}
          {isMobile && (
            <div style={{
              display: 'flex', alignItems: 'center', padding: '10px 12px',
              background: '#fff', borderBottom: '1px solid #DFF1FB',
              position: 'sticky', top: 0, zIndex: 10,
            }}>
              <div style={{ width: 60, display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#004276" strokeWidth="2.5">
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <line x1="3" y1="12" x2="21" y2="12"/>
                    <line x1="3" y1="18" x2="21" y2="18"/>
                  </svg>
                </button>
              </div>
              <div style={{
                flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 800,
                background: 'var(--primary-gradient)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Symptom Checker
              </div>
              <div style={{ width: 60, display: 'flex', justifyContent: 'flex-end' }}>
                {isReadOnly ? (
                  <span style={{ ...s.langBadge, cursor: 'default', opacity: 0.75 }}>
                    {(LANGUAGES.find(l => l.code === activeTTSLang) || LANGUAGES[0]).flag}
                  </span>
                ) : (
                  <button style={{ ...s.langBadge, padding: '4px 8px', fontSize: 11 }} onClick={() => setSelectedLang(null)}>
                    {activeLang.flag}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Desktop chat header */}
          {!isMobile && chatStarted && (
            <div style={s.chatHeader}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h2 style={s.chatHeaderTitle}>Symptom Checker</h2>
                  <p style={s.chatHeaderSub}>AI-Powered Health Assessment Assistant</p>
                </div>
                {isReadOnly ? (
                  <span style={{ ...s.langBadge, cursor: 'default', opacity: 0.75 }}>
                    <span>{(LANGUAGES.find(l => l.code === activeTTSLang) || LANGUAGES[0]).flag}</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      {(LANGUAGES.find(l => l.code === activeTTSLang) || LANGUAGES[0]).label}
                    </span>
                  </span>
                ) : (
                  <button style={s.langBadge} onClick={() => setSelectedLang(null)} title="Change language">
                    <span>{activeLang.flag}</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{activeLang.label}</span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          <div
            ref={chatAreaRef}
            style={{
              ...s.chatArea,
              padding: isMobile ? '16px 12px' : '24px 32px',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {!chatStarted ? (
              <div style={s.welcomeScreen}>
                <div style={s.welcomeTop}>
                  <h1 style={{ ...s.welcomeTitle, fontSize: isMobile ? 26 : 36 }}>Symptom Checker</h1>
                  <p style={{ ...s.welcomeSub, fontSize: isMobile ? 13 : 16 }}>Describe your symptoms and get instant Guidance</p>
                </div>

                <div style={s.robotWrap}>
                  <img src={robotImg} alt="AI Assistant" style={{ ...s.robotImg, width: isMobile ? 110 : 150, height: isMobile ? 110 : 150 }} />
                </div>

                {/* <button style={{ ...s.langBadge, marginBottom: 4 }} onClick={() => setSelectedLang(null)} title="Change language">
                  <span>{activeLang.flag}</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{activeLang.label}</span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button> */}

                {/* ── Inline language selector card with dropdown ── */}
                <div style={{ ...s.langSelectorCard, maxWidth: isMobile ? '100%' : 950}}>
                 <div style={s.langSelectorTitle}>
  <div style={{ marginBottom: 4, textAlign: 'center', color: 'var(--color-brand-primary)' }}>
    Symptom Checker is now available in <strong>8 languages</strong>:
  </div>
  <div style={{ fontWeight: 500, marginBottom: 4, textAlign: 'center', color: 'var(--color-brand-green)' }}>
    <strong>English</strong> • <strong>हिंदी</strong> (Hindi) • <strong>ଓଡ଼ିଆ</strong> (Odia) • <strong>বাংলা</strong> (Bengali) • <strong>தமிழ்</strong> (Tamil) • <strong>తెలుగు</strong> (Telugu) • <strong>मराठी</strong> (Marathi) • <strong>ಕನ್ನಡ</strong> (Kannada)
  </div>
  <div style={{ fontWeight: 400, opacity: 0.8, textAlign: 'center', color: 'var(--color-brand-primary)' }}>
    making healthcare more accessible, inclusive, and closer to every community.
  </div>
</div>
                  {/* Custom styled dropdown */}
                  <div style={{ position: 'relative', width: '100%', maxWidth: 300, margin: '0 auto' }}>
                    <div style={s.langDropdownWrap}>
                      {/* Globe icon */}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        style={{ flexShrink: 0 }}
                        stroke="url(#globeGrad)" strokeWidth="2">
                        <defs>
                          <linearGradient id="globeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#004276"/>
                            <stop offset="100%" stopColor="#004276"/>
                          </linearGradient>
                        </defs>
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      {/* The native select — invisible but functional */}
                      <select
                        value={selectedLang}
                        onChange={e => setSelectedLang(e.target.value)}
                        style={s.langDropdownSelect}
                      >
                        {LANGUAGES.map(({ code, label, native }) => (
                          <option key={code} value={code}>
                            {label}{native !== label ? ` — ${native}` : ''}
                          </option>
                        ))}
                      </select>
                      {/* Visible label overlay */}
                      <span style={s.langDropdownLabel}>
                        {(() => {
                          const l = LANGUAGES.find(l => l.code === selectedLang) || LANGUAGES[0];
                          return l.native !== l.label ? `${l.label} — ${l.native}` : l.label;
                        })()}
                      </span>
                      {/* Chevron */}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="url(#chevGrad)" strokeWidth="2.5" style={{ flexShrink: 0, pointerEvents: 'none' }}>
                        <defs>
                          <linearGradient id="chevGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#004276"/>
                            <stop offset="100%" stopColor="#004276"/>
                          </linearGradient>
                        </defs>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>
                  </div>

                </div>

                <TriageToggle mode={triageMode} onChange={setTriageMode} isMobile={isMobile} />

                <div style={s.welcomeGreet}>
                  <div style={{ ...s.welcomeName, fontSize: isMobile ? 17 : 20 }}>Hello {patientName}!</div>
                  <div style={{ ...s.welcomeQuestion, fontSize: isMobile ? 13 : 15 }}>What Symptoms are you experiencing Today?</div>
                </div>

                <div style={{ ...s.quickSymptoms, gap: isMobile ? 8 : 12 }}>
                  {QUICK_SYMPTOMS.map(({ label, icon }) => (
                    <button key={label} style={{
                      ...s.quickBtn,
                      padding: isMobile ? '9px 14px' : '11px 22px',
                      fontSize: isMobile ? 12 : 14,
                    }}
                      onClick={() => sendMessage(label)}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'var(--primary-gradient-180)';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.border = '1.5px solid transparent';
                        const img = e.currentTarget.querySelector('img');
                        if (img) img.style.filter = 'brightness(0) invert(1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.color = 'var(--color-brand-primary)';
                        e.currentTarget.style.border = '1.5px solid var(--color-brand-primary)';
                        const img = e.currentTarget.querySelector('img');
                        if (img) img.style.filter = 'none';
                      }}>
                      <img src={icon} alt={label} style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28, objectFit: 'contain' }} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => {
                if (msg.type === 'user')     return <UserMessage key={i} text={msg.text} time={msg.time}/>;
                if (msg.type === 'bot')      return <BotMessage  key={i} text={msg.text} chips={msg.chips} onChipClick={sendMessage} time={msg.time} isReadOnly={isReadOnly} lang={activeTTSLang} sharedAudioRef={activeTTSRef} sharedSetPlayingRef={activeTTSSetPlayingRef}/>;
                if (msg.type === 'thinking') return <ThinkingBubble key={i}/>;
                if (msg.type === 'result')   return <ResultCard  key={i} data={msg.data} time={msg.time} lang={activeTTSLang}/>;
                return null;
              })
            )}
          </div>

          <div style={{ ...s.inputArea, padding: isMobile ? '10px 12px 14px' : '12px 28px 16px' }}>
            <div style={{ ...s.inputRow, ...(isReadOnly ? s.inputRowReadOnly : {}) }}>
              <MicButton
                onTranscript={handleTranscript}
                onError={showToast}
                disabled={isLoading || isReadOnly}
                lang={selectedLang}
              />
              {/* Mode of this chat; for a past session it's only shown when known */}
              {(!isReadOnly || viewedMode) && (
                <span style={s.modePill}>{TRIAGE_MODES[isReadOnly ? viewedMode : triageMode].tag}</span>
              )}
              <input
                ref={inputRef}
                className={`caresathi-chat-input${isReadOnly ? ' scb-readonly-input' : ''}`}
                style={{ ...s.textInput, fontSize: isMobile ? 13 : 14 }}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isReadOnly ? 'View only — start a New Chat to continue' : chatStarted ? 'Type or speak your response...' : 'Type or speak your symptom...'}
                disabled={isLoading || isReadOnly}
              />
              <button
                style={{ ...s.sendBtn, opacity: isLoading || !inputValue.trim() || isReadOnly ? 0.4 : 1 }}
                onClick={() => sendMessage()}
                disabled={isLoading || !inputValue.trim() || isReadOnly}>
                <img src={sendSvg} alt="send" style={{ width: 26, height: 26, display: 'block' }} />
              </button>
            </div>
            <p style={{ ...s.disclaimer, fontSize: isMobile ? 10 : 11 }}>CureBay AI is a tool for preliminary assessment and not a substitute for professional medical advice.</p>
          </div>
        </div>

        <style>{`
          @keyframes scb-blink {
            0%,80%,100%{opacity:.3;transform:scale(.8)}
            40%{opacity:1;transform:scale(1)}
          }
          @keyframes scb-float {
            0%,100%{transform:translateY(0)}
            50%{transform:translateY(-10px)}
          }
          @keyframes scb-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes scb-pulse {
            0%,100%{ opacity: 1; transform: scale(1); }
            50%    { opacity: 0.6; transform: scale(0.85); }
          }
          .scb-readonly-input::placeholder { color: #555; opacity: 1; }
        `}</style>

        {toast && (
          <div style={{
            position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--primary-gradient-180)',
            color: '#fff', padding: '12px 24px', borderRadius: 30,
            fontSize: 14, fontWeight: 600, zIndex: 9999,
            boxShadow: '0 4px 20px rgba(0,66,118,0.35)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            🎤 {toast}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const HEADER_HEIGHT = 64;
const s = {
  wrapper: {
    display: 'flex',
    maxHeight: '100dvh',
    overflow: 'hidden',
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: 'var(--section-bg-gradient), #fff',
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    position: 'relative',
  },

  langSelectorCard: {
  background: '#fff',
  border: '1.5px solid #C9EEF8',
  borderRadius: 16, padding: '16px 20px',
  width: '100%', boxSizing: 'border-box',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
  boxShadow: '0px 0px 15px 0px #80DAF1',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
},

langDropdownWrap: {
  display: 'flex', alignItems: 'center', gap: 8,
  background: '#ffffff',
  border: '1.5px solid rgba(0,66,118,0.40)',
  borderRadius: 24, padding: '8px 16px',
  width: '100%', boxSizing: 'border-box',
  position: 'relative', cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,66,118,0.12)',
},
langDropdownSelect: {
  position: 'absolute', inset: 0, opacity: 0,
  width: '100%', height: '100%', cursor: 'pointer',
  fontSize: 14, zIndex: 2,
},

langDropdownLabel: {
  flex: 1, fontSize: 14, fontWeight: 600, pointerEvents: 'none',
  zIndex: 1, position: 'relative',
  background: 'var(--primary-gradient)',
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
},
langSelectorTitle: {
  fontSize: 15, lineHeight: 1.7, textAlign: 'center',
  color: 'var(--color-brand-primary)',
  fontWeight: 400,
  width: '100%',
  display: 'flex', flexDirection: 'column', alignItems: 'center',
},

  modalOverlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'var(--section-bg-gradient), #fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
  },
  modalCard: {
    background: 'rgba(255,255,255,0.98)',
    borderRadius: 24, padding: '32px 24px',
    boxShadow: '0px 0px 15px 0px #80DAF1',
    minWidth: 0, maxWidth: 400, width: '100%',
  },
  modalTitle: {
    fontSize: 22, fontWeight: 800, textAlign: 'center', margin: '0 0 8px',
    background: 'var(--primary-gradient)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  modalSub: { fontSize: 13, color: '#888', textAlign: 'center', margin: 0 },
  langBtn: {
    display: 'flex', alignItems: 'center', gap: 14,
    border: '1.5px solid var(--color-brand-primary)', borderRadius: 14,
    padding: '14px 20px', cursor: 'pointer',
    transition: 'all 0.18s', width: '100%',
  },
  langBadge: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(0,66,118,0.07)',
    border: '1.5px solid rgba(0,66,118,0.2)',
    borderRadius: 20, padding: '5px 12px',
    cursor: 'pointer', color: 'var(--color-brand-primary)',
    fontSize: 12, fontWeight: 600,
    transition: 'all 0.15s',
  },

  sidebar: {
    background: 'rgba(255,255,255,0.82)',
    backdropFilter: 'blur(16px)',
    borderRight: '1px solid #DFF1FB',
    display: 'flex', flexDirection: 'column',
    padding: '16px 12px', gap: 10,
    flexShrink: 0,
    transition: 'width 0.25s ease',
    overflow: 'hidden',
    minHeight: 0,
  },
  sidebarToggle: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 6, borderRadius: 8, alignSelf: 'flex-end',
    display: 'flex', alignItems: 'center',
  },
  newChatBtn: {
    marginTop: 'auto',
    background: 'var(--color-brand-primary)', color: '#fff',
    border: '1.5px solid #FFFFFF', borderRadius: 24,
    padding: '10px 18px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8, whiteSpace: 'nowrap',
    boxShadow: '0px 0px 4px 0px #00000040',
  },
  newChatBtnCollapsed: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'var(--color-brand-primary)', border: '1.5px solid #fff',
    boxShadow: '0px 0px 4px 0px #00000040', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'center',
  },
  historySpacer: { flex: '0 0 auto', height: 0 },
  historySection: {
    flex: 1, overflowY: 'auto', overflowX: 'hidden',
    marginTop: 4, display: 'flex', flexDirection: 'column', minHeight: 0,
  },
  historyGroupHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '6px 4px', cursor: 'pointer', marginBottom: 4,
    background: 'none', border: 'none', width: '100%',
  },
  historyGroupLabelTitle: {
    fontFamily: 'Inter, sans-serif', fontWeight: 600,
    fontSize: '16px', lineHeight: '100%', letterSpacing: '0px',
    color: 'var(--color-brand-primary)', display: 'flex', alignItems: 'center',
  },
  historyGroupLabel2: {
    fontSize: 11, fontWeight: 700, color: 'var(--color-brand-primary)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    padding: '10px 4px 4px',
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(0,66,118,0.05)',
    border: '1.5px solid rgba(0,66,118,0.18)',
    borderRadius: 10, padding: '6px 10px', marginBottom: 6,
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: 12, color: '#333',
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  historyItem: {
    fontSize: 13, color: '#444', padding: '7px 10px',
    borderRadius: 8, cursor: 'pointer', display: 'block',
    width: '100%', textAlign: 'left', background: 'none', border: 'none',
    whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere',
  },
  historyItemActive: {
    background: '#EAF3FA',
    boxShadow: 'inset 3px 0 0 0 var(--color-brand-primary)',
    color: 'var(--color-brand-primary)',
  },
  historyEmpty: { fontSize: 12, color: '#bbb', padding: '6px 10px' },

  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 },
  chatHeader: {
    padding: '14px 28px',
    background: 'rgba(255,255,255,0.55)',
    backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #DFF1FB',
  },
  chatHeaderTitle: { fontSize: 20, fontWeight: 800, color: 'var(--color-brand-primary)', margin: 0 },
  chatHeaderSub: { fontSize: 12, color: '#888', margin: '2px 0 0' },
  chatArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex', flexDirection: 'column', gap: 18,
    paddingBottom: '10px',
  },

  // Centred via auto margins on the first/last child (welcomeTop / quickSymptoms):
  // `justifyContent: center` clips the heading once the content is taller than the box.
  welcomeScreen: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'flex-start',
    // Grow to fit content (no squeezing); the chat area handles scrolling.
    flex: '1 0 auto', gap: 12, padding: '8px 16px',
    boxSizing: 'border-box', width: '100%',
  },
  welcomeTop: { textAlign: 'center', width: '100%', marginTop: 'auto' },
  welcomeTitle: {
    fontSize: 36, fontWeight: 600, fontFamily: 'Inter, sans-serif',
    lineHeight: '40px',
    background: 'var(--primary-gradient)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text', margin: '0 0 8px',
  },
  welcomeSub: { fontSize: 16, fontWeight: 600, fontFamily: 'Inter, sans-serif', color: 'var(--color-brand-primary)', margin: 0 },
  robotWrap: { margin: '2px 0' },
  robotImg: {
    width: 190, height: 190, objectFit: 'contain',
    animation: 'scb-float 3s ease-in-out infinite', display: 'block',
  },
  welcomeGreet: { textAlign: 'center', lineHeight: 1.6 },
  welcomeName: { fontSize: 20, fontWeight: 700, color: '#1a1a2e' },
  welcomeQuestion: { fontSize: 15, color: '#333' },
  quickSymptoms: {
    display: 'flex', gap: 12, flexWrap: 'wrap',
    justifyContent: 'center', marginTop: 4, marginBottom: 'auto',
    width: '100%', maxWidth: '100%', padding: '0 4px', boxSizing: 'border-box',
  },
  quickBtn: {
    border: '1.5px solid var(--color-brand-primary)', background: '#fff', color: 'var(--color-brand-primary)',
    fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif',
    borderRadius: 28, padding: '11px 22px', cursor: 'pointer',
    transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 10,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)', flexShrink: 0,
  },

  // flexShrink 0: rows sit in the column-flex chat area and must never be squeezed (clipped) to fit
  msgRow: { display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', flexShrink: 0 },
  msgCol: { display: 'flex', flexDirection: 'column', maxWidth: '80%', minWidth: 0 },
  msgColUser: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '80%', minWidth: 0 },
  bubble: {
    padding: '12px 16px', borderRadius: 16, fontSize: 14, lineHeight: 1.65,
    wordBreak: 'break-word', overflowWrap: 'anywhere',
  },
  bubbleBot: {
    background: 'rgba(255,255,255,0.92)', color: '#222',
    borderRadius: '4px 16px 16px 16px',
    boxShadow: '0 1px 8px rgba(0,0,0,0.08)', backdropFilter: 'blur(4px)',
  },
  bubbleUser: {
    background: 'var(--color-brand-primary)',
    color: '#fff', borderRadius: '16px 4px 16px 16px',
    display: 'flex', alignItems: 'flex-start', gap: 8,
  },
  msgTime: { fontSize: 11, color: '#999', marginTop: 4 },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
  chip: {
    border: '1.5px solid var(--color-brand-primary)', background: '#fff', color: 'var(--color-brand-primary)',
    fontSize: 13, fontWeight: 500, borderRadius: 20,
    padding: '7px 16px', cursor: 'pointer', transition: 'all 0.15s',
  },

  ttsBtn: {
    background: 'rgba(0,66,118,0.08)', border: '1.5px solid rgba(0,66,118,0.25)',
    cursor: 'pointer', padding: '6px 8px', borderRadius: 8, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'color 0.15s, opacity 0.15s', marginTop: 1, color: 'var(--color-brand-primary)',
  },
  micBtn: {
    width: 34, height: 34, borderRadius: '50%',
    cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s', padding: 0,
  },

  analyzingText: { fontSize: 13, color: '#555', marginBottom: 8 },
  thinkingDots: { display: 'flex', gap: 5, alignItems: 'center' },
  dot: {
    width: 8, height: 8, background: 'var(--color-brand-green)', borderRadius: '50%',
    display: 'inline-block', animation: 'scb-blink 1.2s infinite',
  },

  resultCard: {
    background: '#F1F8FD', borderRadius: 16,
    padding: '16px 14px', border: '1px solid #DFF1FB',
    boxShadow: '0px 4px 10px 0px #80DAF180',
    width: '100%', boxSizing: 'border-box', overflowX: 'hidden',
  },
  summaryBox: { background: '#fff', borderRadius: 10, padding: '10px 14px', marginBottom: 16, border: '1px solid #DFF1FB' },
  summaryLabel: { fontSize: 12, fontWeight: 600, color: 'var(--color-brand-primary)', display: 'block', marginBottom: 4 },
  summaryText: { fontSize: 13, color: '#444', lineHeight: 1.6, margin: 0 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 10 },
  severityTrack: {
    height: 16, borderRadius: 20,
    background: 'linear-gradient(to right,#22c55e 0%,#eab308 45%,#f97316 65%,#ef4444 100%)',
    position: 'relative', marginBottom: 4,
  },
  severityThumb: {
    position: 'absolute', top: '50%', transform: 'translate(-50%,-50%)',
    width: 22, height: 22, borderRadius: '50%',
    background: '#fff', border: '3px solid #888', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
  },
  severityLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' },
  severityLevel: { textAlign: 'center', fontSize: 13, fontWeight: 700, marginTop: 4 },
  diagnosisGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16,
  },
  diagItem: { background: '#fff', borderRadius: 10, padding: '10px 12px', border: '1px solid #DFF1FB', minWidth: 0 },
  diagBarTrack: { height: 5, background: '#eee', borderRadius: 3, overflow: 'hidden' },
  diagBarFill: { height: '100%', borderRadius: 3 },
  recRow: {
    display: 'flex', alignItems: 'center',
    background: '#EAF3FA',
    borderRadius: 12, padding: '12px 14px', gap: 12, marginBottom: 4,
    border: '1px solid #DFF1FB', flexWrap: 'wrap',
  },
  recText: { fontSize: 13, color: '#222', lineHeight: 1.5, flex: 1, minWidth: 120 },
  bookBtn: {
    background: 'var(--primary-gradient-180)',
    color: '#fff', border: 'none', borderRadius: 20,
    padding: '9px 18px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'opacity 0.15s',
  },
  remedyItem: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: '#fff', borderRadius: 10, padding: '10px 14px', border: '1px solid #DFF1FB',
  },
  remedyDot: {
    width: 22, height: 22, borderRadius: '50%',
    background: 'var(--primary-gradient-135)',
    color: '#fff', fontSize: 12, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  hospitalItem: { background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #DFF1FB' },

  inputArea: {
    background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)',
    borderTop: '1px solid #DFF1FB',
    paddingBottom: 'env(safe-area-inset-bottom)', flexShrink: 0,
  },
  inputRow: { display: 'flex', alignItems: 'center', padding: '8px 8px 8px 12px', gap: 8 },
  modePill: {
    flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-brand-primary)',
    background: '#E3EEF8', borderRadius: 999, padding: '4px 12px',
  },

  // Quick / Detailed triage toggle
  triageWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%' },
  triageLabel: { fontSize: 12, fontWeight: 500, color: '#5B6675' },
  triageTrack: {
    display: 'flex', gap: 6, padding: 5, borderRadius: 999, background: '#EAF4FB',
    border: '1px solid #CDEBF5', boxShadow: '0 0 0 4px rgba(128,218,241,0.15)',
  },
  triageBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '9px 12px', borderRadius: 999, border: 'none', background: 'transparent',
    color: 'var(--color-brand-primary)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'background 0.15s, color 0.15s',
  },
  triageBtnOn: { background: 'var(--color-brand-primary)', color: '#fff', boxShadow: '0 2px 8px rgba(0,66,118,0.25)' },
  triageIcon: {
    width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#fff', color: 'var(--color-brand-primary)', flexShrink: 0,
  },
  triageIconOn: { background: 'var(--color-brand-green)', color: '#fff' },
  triageTip: {
    position: 'absolute', top: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)', zIndex: 20,
    width: 220, padding: '8px 12px', borderRadius: 8, background: '#E6F2FB', border: '1px solid #9CC9EA',
    fontSize: 11.5, lineHeight: 1.45, color: 'var(--color-brand-primary)', textAlign: 'left',
    boxShadow: '0 6px 18px rgba(0,66,118,0.12)', pointerEvents: 'none',
  },
  triageHintMobile: { fontSize: 11.5, color: '#5B6675', textAlign: 'center', maxWidth: 320, lineHeight: 1.45 },
  historyModeTag: {
    flexShrink: 0, fontSize: 10, fontWeight: 600, color: 'var(--color-brand-primary)',
    background: '#E3EEF8', borderRadius: 4, padding: '1px 6px', marginTop: 1,
  },
  inputRowReadOnly: { opacity: 0.6 },
  textInput: {
    flex: 1, border: 'none', outline: 'none',
    borderBottom: '1.5px solid rgba(0,66,118,0.25)',
    fontSize: 14, color: '#333', background: 'transparent',
    padding: '8px 0', lineHeight: '1.5', height: 'auto', minHeight: 36,
    boxSizing: 'border-box', minWidth: 0,
  },
  sendBtn: {
    width: 36, height: 36, background: 'transparent', border: 'none',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'opacity 0.15s', padding: 6,
  },
  disclaimer: { fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 6, marginBottom: 0 },
};

export default SymptomCheckerChat;